import React from 'react';
import { compact, map, reduce } from 'lodash';
import { useParams } from 'react-router-dom';
import { UserRoomLink, User, Room as IRoom, GameStatus } from '../types';
import { Chat } from './Chat';
import { WordSkeleton } from './WordSkeleton';
import { Sketch } from './Sketch';
import { ProgressCircle } from './ProgressCircle';

import logger from 'use-reducer-logger';

import { getUserRoomLinksForRoomId } from '../firestore/queries';
import { getUser } from '../firestore/user';
import { createUserRoomLink } from '../firestore/userRoomLink';
import { CurrentUserContext } from '../contexts/CurrentUserContext';
import { getRoomRef, endDrawer, roundLength } from '../firestore/room';
import styled from 'styled-components';
import { Leaderboard } from './Leaderboard';

function usersReducer(state: { [id: string]: User }, action: any) {
    switch (action.type) {
        case 'SET_USERS':
            return action.payload.users;
        default:
            return state;
    }
}

function useEpoch() {
    const [epoch, setEpoch] = React.useState();

    React.useEffect(() => {
        window.requestAnimationFrame(function generateEpoch() {
            setEpoch(new Date().getTime());
            window.requestAnimationFrame(generateEpoch);
        });
    }, []);

    return epoch;
}

const EndDrawer = ({ id, primaryUserId, roundEndTime, gameStatus }: Partial<IRoom>) => {
    const epoch = useEpoch();
    const { currentUserId } = React.useContext(CurrentUserContext);
    React.useEffect(() => {
        if (
            id &&
            primaryUserId === currentUserId &&
            roundEndTime &&
            gameStatus === GameStatus.RoundInProgress
        ) {
            if (epoch > roundEndTime) {
                endDrawer(id);
            }
        }
    }, [epoch, id, currentUserId, gameStatus, primaryUserId, roundEndTime]);
    return null;
};

const EndRoundButton = ({ gameStatus, roundEndTime, id }: Partial<IRoom>) => {
    const epoch = useEpoch();
    return gameStatus === GameStatus.RoundInProgress &&
        !!roundEndTime &&
        ((roundEndTime - epoch) * 100) / roundLength < 0 ? (
        <button onClick={() => endDrawer(id || '')}>Force End Round</button>
    ) : null;
};

const DisplayedOrHiddenProgressCircle = ({ gameStatus, roundEndTime }: Partial<IRoom>) => {
    const epoch = useEpoch();
    return gameStatus === GameStatus.RoundInProgress && !!roundEndTime ? (
        <StyledProgressCircle
            percent={((roundEndTime - epoch) * 100) / roundLength}
            total={roundLength}
        />
    ) : null;
};

const RoomContainer = styled.div`
    display: flex;
    overflow: hidden;
`;

const RightContainer = styled.div`
    display: flex;
    overflow: hidden;
    width: 300px;
    flex-direction column;
`;

const LeftContainer = styled.div`
    display: flex;
    overflow: hidden;
    flex: 1;
`;

const SketchContainer = styled.div`
    position: relative;
    border: 1px dashed black;
`;

const RoundInfoContainer = styled.div`
    display: flex;
    align-items: center;
`;

const RoundInfo = styled.div`
    margin-left: 4px;
`;

const StyledProgressCircle = styled(ProgressCircle)`
    width: 50px;
    height: 50px;
`;

// TODO: handle redundancy when starting a new game
export const Room = () => {
    const { currentUserId } = React.useContext(CurrentUserContext);
    const { roomId } = useParams();

    const [users, dispatch] = React.useReducer(logger(usersReducer), {});

    const [room, setRoom] = React.useState<IRoom | undefined>();

    // Create link when entering room
    React.useEffect(() => {
        createUserRoomLink(currentUserId, roomId || '');
    }, [currentUserId, roomId]);

    // Listen to some room details
    React.useEffect(() => {
        const cb = (dataSnapshot: firebase.database.DataSnapshot) => {
            setRoom(dataSnapshot.val());
        };
        const roomRef = getRoomRef(roomId || '');
        roomRef.on('value', cb);
        return () => roomRef.off('value', cb);
    }, [roomId]);

    React.useEffect(() => {
        // Figure out what users are in this room
        const query = getUserRoomLinksForRoomId(roomId || '');
        const cb = (snapshot: firebase.database.DataSnapshot) => {
            // Handle the different change events
            const userRoomLinks: UserRoomLink[] = snapshot.val();

            // Get all the users
            Promise.all(
                map(userRoomLinks, ({ userId }) => {
                    return getUser(userId);
                }),
            ).then((users) => {
                dispatch({
                    type: 'SET_USERS',
                    payload: {
                        users: reduce(
                            compact(users),
                            (acc: { [id: string]: User }, user) => {
                                acc[user.id] = user;
                                return acc;
                            },
                            {},
                        ),
                    },
                });
            });
        };
        query.on('value', cb);
        return () => query.off('value', cb);
    }, [currentUserId, roomId]);

    if (!roomId) {
        return null;
    }

    if (!room) {
        return null;
    }

    const currentDrawer = room?.drawer && users ? users[room.drawer] : undefined;

    return (
        <RoomContainer>
            <EndDrawer
                id={room.id}
                primaryUserId={room.primaryUserId}
                roundEndTime={room.roundEndTime}
                gameStatus={room.gameStatus}
            />
            <LeftContainer>
                <Chat
                    roomId={roomId}
                    answer={room.answer}
                    users={users}
                    allowGuess={
                        room.gameStatus === GameStatus.RoundInProgress &&
                        currentUserId !== room.drawer
                    }
                />
            </LeftContainer>
            <RightContainer>
                <SketchContainer>
                    <Sketch
                        gameStatus={room.gameStatus}
                        roomId={roomId}
                        drawer={currentDrawer}
                        resetDrawing={room.gameStatus === GameStatus.RoundInProgress}
                    />
                    <EndRoundButton />
                    {!!room.answer &&
                        (room.gameStatus === GameStatus.RoundInProgress ||
                            room.gameStatus === GameStatus.WaitingToStartNextDrawer) && (
                            <WordSkeleton
                                word={room.answer}
                                showWord={
                                    currentUserId === room.drawer ||
                                    room.gameStatus === GameStatus.WaitingToStartNextDrawer
                                }
                            />
                        )}
                    <RoundInfoContainer>
                        <DisplayedOrHiddenProgressCircle
                            gameStatus={room.gameStatus}
                            roundEndTime={room.roundEndTime}
                            id={roomId}
                        />
                        <RoundInfo>
                            <div>Round: {room.round}</div>
                        </RoundInfo>
                    </RoundInfoContainer>
                </SketchContainer>
                <Leaderboard
                    users={users}
                    correctUsers={room.correctUsers || {}}
                    showRoundScores={room.gameStatus === GameStatus.WaitingToStartNextDrawer}
                    scores={room.scores || {}}
                />
            </RightContainer>
        </RoomContainer>
    );
};
